import { CheckIcon, LockClosedIcon } from '@heroicons/react/20/solid';
import {
    CourtierEmailWarning,
    FieldInput,
    FieldRadioBlock,
    Modal,
    Question,
    useCourtierEmailBlock,
} from '@workspace/core-ts/components';
import { SmartFormGlobal, useGlobalForm, useSmartFormGlobal } from '@workspace/core-ts/contexts';
import { CONTACT_CONSENT_APPORTEURS } from '@workspace/core-ts/hooks';
import { useState } from 'react';

/* Liens et nom affichés selon le gestionnaire : APRIL Moto (direct) ou AMGestion */
const MANDANTS = {
    aprilMoto: {
        nom: 'APRIL Moto',
        politiqueCookies: 'https://cms-api.april-moto.com/storage/documents/politique-cookies-et-technologies-similaires-0926.pdf',
        lettreRgpd: 'https://devis.april-moto.com/mentions-legales/donnees-personnelles.html',
    },
    amGestion: {
        nom: 'AMGestion',
        politiqueCookies: 'https://cms-api.april-moto.com/storage/documents/politique-cookies-et-technologies-similaires-0926.pdf',
        lettreRgpd: 'https://www.amgestionassurance.com/Doc/Lettre-RGPD-AM-Gestion-11.2021.pdf',
    },
};

const OPTIONS_CONSENTEMENT = [
    { value: true, label: "J’accepte" },
    { value: false, label: 'Je refuse' },
];

const VALIDATION_CONSENTEMENT = {
    validate: (value: unknown) => value === true || value === false || 'Merci de renseigner vos préférences.',
};

/**
 * Condition d’affichage de l’étape (à utiliser dans `showWhen`) : mêmes apporteurs que le consentement
 * e-mail de l’étape Coordonnées, hors avenant.
 */
export const showConsentementEmailStep = (state: any, initialState: any) => {
    const donnees = initialState ?? state;
    return (
        donnees?.DemandeTarif?.ActeGestion !== 'AV' &&
        CONTACT_CONSENT_APPORTEURS.includes(Number(donnees?.DemandeTarif?.Police?.IdApporteur1))
    );
};

/**
 * Le consentement relances / offres a déjà été recueilli par l’étape « Votre devis est presque prêt »
 * (PixelConsent renseigné) : les étapes suivantes n’ont pas à le redemander.
 */
export const isConsentementEmailRecueilli = (pixelConsent: unknown) => pixelConsent === true || pixelConsent === false;

function LienExterne({ href, className, children }: { href: string; className?: string; children: React.ReactNode }) {
    return (
        <a href={href} target="_blank" rel="noopener noreferrer" className={className}>
            {children}
            <span className="sr-only"> (nouvelle fenêtre)</span>
        </a>
    );
}

function ListeCoche({ items }: { items: string[] }) {
    return (
        <ul className="space-y-2">
            {items.map((item) => (
                <li key={item} className="flex items-start gap-2">
                    <CheckIcon className="w-5 h-5 text-secondary flex-shrink-0 mt-0.5" aria-hidden="true" />
                    <span>{item}</span>
                </li>
            ))}
        </ul>
    );
}

/**
 * Pop-in d’information sur les pixels et liens traçants présents dans les e-mails.
 */
export function PixelInfoModal({ open, onClose, politiqueCookies }: { open: boolean; onClose: () => void; politiqueCookies: string }) {
    return (
        <Modal open={open} onClose={onClose} title="Pixels & liens traçants : à quoi ça sert ?" className="max-w-xl" closeButton>
            <Modal.Body className="pb-6 space-y-3 text-sm">
                <h2 className="font-bold text-primary mt-2">Le pixel</h2>
                <p>
                    C’est une toute petite image, invisible à l’œil nu, glissée dans certains de nos e-mails. Elle ne contient aucune
                    donnée sensible : elle nous indique simplement <strong>si le message a été ouvert</strong>.
                </p>

                <h2 className="font-bold text-primary">Les liens traçants</h2>
                <p>
                    Certains liens de nos e-mails contiennent un petit code qui nous indique simplement <strong>si vous avez cliqué dessus</strong>.
                    Le lien vous emmène toujours exactement là où il l’annonce : rien ne change pour vous au clic.
                </p>

                <hr className="my-4" />

                <h2 className="font-bold text-primary uppercase">Ce que ça nous permet de faire</h2>
                <ListeCoche
                    items={[
                        'Savoir si nos e-mails vous sont utiles, et quels sujets vous intéressent',
                        'Éviter de vous envoyer des messages qui ne vous intéressent pas',
                        'Améliorer, petit à petit, la pertinence de nos communications',
                    ]}
                />

                <div className="rounded-xl bg-green-50 text-green-800 p-4 space-y-2">
                    <h2 className="font-bold uppercase">Ce que ça ne fait jamais</h2>
                    <ul className="space-y-1">
                        {[
                            'Ne collecte aucune donnée personnelle sensible',
                            'Ne vous suit jamais en dehors de vos e-mails',
                            "N’est jamais revendu ni partagé avec un tiers",
                        ].map((item) => (
                            <li key={item} className="flex items-start gap-2">
                                <LockClosedIcon className="w-4 h-4 flex-shrink-0 mt-0.5" aria-hidden="true" />
                                <span>{item}</span>
                            </li>
                        ))}
                    </ul>
                </div>

                <p className="text-center text-gray-600">
                    Vous gardez la main : vous pouvez changer d’avis à tout moment depuis vos préférences, sans que ça n’affecte votre devis.
                </p>
                <p className="text-center text-gray-600">
                    Pour en savoir plus, consultez notre{' '}
                    <LienExterne href={politiqueCookies} className="underline font-medium">
                        Politique cookies et technologies similaires
                    </LienExterne>
                    .
                </p>

                <button type="button" onClick={onClose} className="w-full rounded-full bg-primary text-white font-semibold py-3 mt-2">
                    J’ai compris
                </button>
            </Modal.Body>
        </Modal>
    );
}

/**
 * Étape « Votre devis est presque prêt », juste après le tarif : e-mail du client (avec confirmation),
 * consentement relances / offres (EmailContactConsent) et consentement pixels / liens traçants (PixelConsent).
 * L’e-mail est repris sur l’étape Coordonnées, qui envoie le tout au save_devis.
 */
export function ConsentementEmailStep() {
    const methods = useSmartFormGlobal();
    const { others } = useGlobalForm();
    const { validateNotCourtierEmail } = useCourtierEmailBlock();
    const [popinAffichee, setPopinAffichee] = useState(false);
    const { nom, politiqueCookies, lettreRgpd } = others?.gestionnaire?.codeBelair === 43397 ? MANDANTS.aprilMoto : MANDANTS.amGestion;

    return (
        <SmartFormGlobal
            {...methods}
            className="mx-auto"
        >
            <Question buttonLabel="Continuer mon devis">
                <Question.Header>
                    <Question.Title>Votre devis est presque prêt</Question.Title>
                    <Question.SubTitle>Où souhaitez-vous le recevoir ?</Question.SubTitle>
                    <Question.Description>
                        Indiquez votre adresse e-mail pour recevoir votre devis et pouvoir le retrouver facilement.
                    </Question.Description>
                </Question.Header>

                <FieldInput
                    label="Adresse e-mail"
                    name="DemandeTarif.ListePersonnes[0].Email"
                    type="email"
                    autoComplete="email"
                    placeholder="prenom.nom@email.com"
                    validation={{
                        required: "L’adresse email est requise",
                        validate: {
                            notCourtierEmail: validateNotCourtierEmail,
                        },
                    }}
                />

                <CourtierEmailWarning />

                <FieldInput
                    label="Confirmez votre adresse e-mail"
                    name="DemandeTarif.ListePersonnes[0].ConfirmEmail"
                    type="email"
                    autoComplete="email"
                    placeholder="Saisissez à nouveau votre adresse"
                    validation={{
                        required: "La confirmation de l’email est requise",
                        validate: {
                            isSame: (value) => value === methods.watch('DemandeTarif.ListePersonnes.0.Email') || 'Les adresses e-mail ne correspondent pas',
                        },
                    }}
                />

                <p className="flex items-start gap-2 text-sm text-gray-600">
                    <LockClosedIcon className="w-4 h-4 flex-shrink-0 mt-0.5" aria-hidden="true" />
                    <span>Votre adresse est utilisée pour vous envoyer et retrouver votre devis.</span>
                </p>

                <div className="w-full rounded-2xl border border-gray-200 bg-white p-5 sm:p-7 space-y-5 text-left">
                    <div>
                        <p className="text-lg font-bold text-primary">C’est vous qui décidez.</p>
                        <p className="text-sm text-gray-600">
                            Nous avons besoin de votre e-mail pour votre devis. Pour le reste, c’est vous qui choisissez.
                        </p>
                    </div>

                    <hr />

                    <fieldset className="space-y-2">
                        <legend className="font-semibold">Recevoir des relances et offres {nom}*</legend>
                        <p className="text-sm text-gray-600">
                            Pour retrouver plus facilement votre devis et recevoir des offres susceptibles de vous intéresser. Désinscription
                            possible à tout moment.
                        </p>
                        <FieldRadioBlock
                            name="DemandeDevis.EmailContactConsent"
                            options={OPTIONS_CONSENTEMENT}
                            classNameGroup="flex-row"
                            validation={VALIDATION_CONSENTEMENT}
                        />
                    </fieldset>

                    <hr />

                    <fieldset className="space-y-2">
                        <legend className="font-semibold">Améliorer les e-mails que je reçois*</legend>
                        <p className="text-sm text-gray-600">
                            J’accepte qu’{nom} mesure l’ouverture, les clics et l’utilisation de ses e-mails afin d’améliorer ses communications.{' '}
                            <button
                                type="button"
                                onClick={() => setPopinAffichee(true)}
                                className="font-semibold text-primary underline"
                            >
                                En savoir plus sur mes préférences ›
                            </button>
                        </p>
                        <FieldRadioBlock
                            name="DemandeDevis.PixelConsent"
                            options={OPTIONS_CONSENTEMENT}
                            classNameGroup="flex-row"
                            validation={VALIDATION_CONSENTEMENT}
                        />
                    </fieldset>
                </div>

                <p className="text-center text-xs text-gray-500">*champs obligatoires</p>
                <p className="text-center text-xs">
                    <LienExterne href={lettreRgpd} className="text-gray-500 underline">
                        Comment utilisons-nous vos données ?
                    </LienExterne>
                </p>
            </Question>

            <PixelInfoModal open={popinAffichee} onClose={() => setPopinAffichee(false)} politiqueCookies={politiqueCookies} />
        </SmartFormGlobal>
    );
}
